import React, { useContext, useState } from 'react'
import axios from 'axios'
import { observer } from 'mobx-react'
import { StoreContext } from '../../App'
import { ButtonText, IntroParagraph } from '../../../styles/generic.styles'
import { Header, OtpInput, Button, LoadingSpinner } from '../../Generic'
import {
  ContentContainerBottomFixed,
  Margined,
  MobileSpacerForContentContainerBottomFixed,
} from '../../../styles/layout.styles'
import { CodeInputWrapper } from '../GoToMobile.styles'
import { forceRedirect } from '../../../methods/forceRedirect'
import { devLog } from '../../../methods/devLog'

interface EnterPinProps {
  nextStep: () => void
  maxAttempts: number
  verifyPinCodeUrl: string
  closeMobileSessionUrl: string
}

export const EnterPin = observer(
  ({
    nextStep,
    maxAttempts,
    verifyPinCodeUrl,
    closeMobileSessionUrl,
  }: EnterPinProps) => {
    const store = useContext(StoreContext)
    const { theme } = store.InterfaceState
    const { SwitchToMobile: trans } = store.TranslationsState.translations

    const [isCodeBeingChecked, setIsCodeBeingChecked] = useState(false)
    const [isBeingRedirected, setIsBeingRedirected] = useState(false)
    const [isCodeIncorrect, setIsCodeIncorrect] = useState(false)
    const [attemptsLeft, setAttemptsLeft] = useState(maxAttempts)

    const [pin, setPin] = useState('')

    const handlePinSubmit = async () => {
      setIsCodeBeingChecked(true)
      try {
        const { data } = await axios.post(
          verifyPinCodeUrl,
          { pinCode: pin },
          { withCredentials: true }
        )

        if (data?.shouldCloseSession) {
          setIsBeingRedirected(true)
          forceRedirect(closeMobileSessionUrl)
        } else if (data?.isPinCodeCorrect) {
          setIsBeingRedirected(true)
          forceRedirect(data.completeProceedUrl)
        } else if (attemptsLeft > 0) {
          setIsCodeIncorrect(true)
          setPin('')
          setAttemptsLeft(attemptsLeft - 1)
        } else {
          setIsBeingRedirected(true)
          nextStep()
        }
      } catch (error) {
        devLog(error)
      }
      setIsCodeBeingChecked(false)
    }

    return (
      <>
        <Header
          {...theme.header}
          fontFamily={theme.globals.fontFamilyHeadline}
          margin="30px 0"
        >
          {trans.enterPin.header}
        </Header>

        <IntroParagraph textAlign="center">
          {trans.enterPin.description}
        </IntroParagraph>

        <Margined margin="0 0 30px" />

        <CodeInputWrapper
          isLoading={isCodeBeingChecked}
          isError={isCodeIncorrect}
        >
          <OtpInput
            value={pin}
            valueLength={4}
            onChange={(val) => {
              setPin(val)
              setIsCodeIncorrect(false)
            }}
            width="190px"
            columnGap="10px"
            marginTop="0"
            marginBottom="0"
          />

          {isCodeIncorrect && (
            <IntroParagraph> {trans.enterPin.error1}</IntroParagraph>
          )}

          {attemptsLeft < maxAttempts && attemptsLeft > 1 && (
            <span>
              {trans.enterPin.attemptsLeft.replace(
                '<number>',
                attemptsLeft.toString()
              )}
            </span>
          )}

          {attemptsLeft === 1 && <span>{trans.enterPin.lastAttempt}</span>}
        </CodeInputWrapper>

        <ContentContainerBottomFixed borderTop="none">
          {!isCodeBeingChecked && !isBeingRedirected ? (
            <Button
              disabled={isCodeBeingChecked || pin.length < 4}
              onClick={handlePinSubmit}
              {...theme.button}
              width="190px"
              paddingMobile="14px 10px"
              margin="0 0 20px 0"
            >
              <ButtonText>{trans.enterPin.continue}</ButtonText>
            </Button>
          ) : (
            <LoadingSpinner
              width="40px"
              bgColor="blue"
              padding="100px 0 40px 0"
              height="0"
              {...theme.loadingSpinner}
            />
          )}
        </ContentContainerBottomFixed>

        <MobileSpacerForContentContainerBottomFixed />
      </>
    )
  }
)
